/**
 * UranIA PA — API client gestione utenti per tenant.
 *
 * Wrapper su:
 *   - ``GET    /api/v1/admin/tenants/{tid}/users``                       (list)
 *   - ``POST   /api/v1/admin/tenants/{tid}/users``                       (create)
 *   - ``PUT    /api/v1/admin/tenants/{tid}/users/{uid}``                 (update)
 *   - ``POST   /api/v1/admin/tenants/{tid}/users/{uid}:reset-password``  (reset pwd)
 *   - ``DELETE /api/v1/admin/tenants/{tid}/users/{uid}``                 (delete)
 *
 * Tutti gli endpoint richiedono ruolo ``platform_admin`` (qualsiasi tenant)
 * o ``tenant_admin`` (solo il proprio tenant), con step-up MFA RFC 9470
 * (bypassabile in dev via env ``URANIA_SKIP_STEP_UP=true``).
 */

import { useAuthStore } from '../auth/auth-store.ts';
import { loadSpaEnv } from '../env.ts';

export type AssignableRole =
  | 'funzionario_adg'
  | 'beneficiario_ri'
  | 'auditor'
  | 'tenant_admin';

export interface TenantUserDto {
  readonly user_id: string;
  readonly username: string;
  readonly email: string;
  readonly first_name: string;
  readonly last_name: string;
  readonly enabled: boolean;
  readonly role: string;
  readonly roles: readonly string[];
  readonly created_at: string | null;
}

export interface ListTenantUsersDto {
  readonly items: readonly TenantUserDto[];
  readonly total: number;
}

export interface CreateTenantUserBody {
  readonly first_name: string;
  readonly last_name: string;
  readonly email: string;
  readonly password: string;
  readonly role: AssignableRole;
}

export interface UpdateTenantUserBody {
  readonly first_name: string;
  readonly last_name: string;
  readonly email: string;
  readonly role: AssignableRole;
  readonly enabled: boolean;
}

export interface ResetPasswordBody {
  readonly new_password: string;
}

export class TenantUsersApiError extends Error {
  public readonly status: number;
  public readonly detail: string | null;
  constructor(status: number, title: string, detail: string | null) {
    super(title);
    this.name = 'TenantUsersApiError';
    this.status = status;
    this.detail = detail;
  }
}

function authHeader(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  if (token === null || token.length === 0) return {};
  return { Authorization: `Bearer ${token}` };
}

function buildUrl(path: string): string {
  const env = loadSpaEnv();
  const base = env.apiBaseUrl.replace(/\/+$/u, '');
  return `${base}${path}`;
}

async function parseProblem(response: Response): Promise<TenantUsersApiError> {
  let title = response.statusText || 'http_error';
  let detail: string | null = null;
  try {
    const body = (await response.json()) as { title?: unknown; detail?: unknown };
    if (typeof body.title === 'string' && body.title.length > 0) title = body.title;
    if (typeof body.detail === 'string') detail = body.detail;
    // FastAPI 422 validation surface: detail e' un array di errori. Riassumo il primo.
    if (Array.isArray(body.detail) && body.detail.length > 0) {
      const first = body.detail[0] as { msg?: unknown };
      if (typeof first.msg === 'string') detail = first.msg;
    }
  } catch {
    // no JSON
  }
  return new TenantUsersApiError(response.status, title, detail);
}

async function jsonRequest<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const init: RequestInit = {
    method,
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...authHeader(),
    },
    credentials: 'include',
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  };
  const response = await fetch(buildUrl(path), init);
  if (!response.ok) throw await parseProblem(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export async function listTenantUsers(
  tenantId: string,
): Promise<ListTenantUsersDto> {
  return jsonRequest<ListTenantUsersDto>(
    'GET',
    `/api/v1/admin/tenants/${encodeURIComponent(tenantId)}/users`,
  );
}

export async function createTenantUser(
  tenantId: string,
  body: CreateTenantUserBody,
): Promise<TenantUserDto> {
  return jsonRequest<TenantUserDto>(
    'POST',
    `/api/v1/admin/tenants/${encodeURIComponent(tenantId)}/users`,
    body,
  );
}

export async function updateTenantUser(
  tenantId: string,
  userId: string,
  body: UpdateTenantUserBody,
): Promise<TenantUserDto> {
  return jsonRequest<TenantUserDto>(
    'PUT',
    `/api/v1/admin/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}`,
    body,
  );
}

export async function resetTenantUserPassword(
  tenantId: string,
  userId: string,
  body: ResetPasswordBody,
): Promise<void> {
  await jsonRequest<void>(
    'POST',
    `/api/v1/admin/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}:reset-password`,
    body,
  );
}

export async function deleteTenantUser(
  tenantId: string,
  userId: string,
): Promise<void> {
  await jsonRequest<void>(
    'DELETE',
    `/api/v1/admin/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}`,
  );
}
